import { lazy, Suspense, useId, useMemo, useState } from 'react'
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LabelList, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  Treemap, ReferenceDot,
} from 'recharts'
import {
  Map as MapIcon, Orbit, Hexagon, Waypoints, ChartColumn, ChartPie, Rainbow, Flower2, LayoutGrid, Grid3x3,
  Gauge, ChartArea, ListOrdered, Table2, Sparkles, ClipboardCheck, Flame,
} from 'lucide-react'
import { themeFor, statusKind, STATUS_LOOK, additiveName } from './dashboardTheme.js'
import { looksLikeCountries, groupByRegion } from './countries.js'
import { Donut, Arch, Rose } from './charts/Donuts.jsx'
import RadialBars from './charts/RadialBars.jsx'
import { leaderInsight, regionInsight, trendInsight, rankInsight, shareInsight, ratingInsight } from './charts/insight.js'
import { regionFlow, seriesFlow, linkFlow } from './charts/flows.js'

// Each of these, and what it needs (map shapes, layout code), is only fetched when it is shown.
const WorldMap = lazy(() => import('./WorldMap.jsx'))
const RegionWheel = lazy(() => import('./charts/RegionWheel.jsx'))
const Mosaic = lazy(() => import('./charts/Mosaic.jsx'))
const Flow = lazy(() => import('./charts/Flow.jsx'))
const RibbonRank = lazy(() => import('./charts/RibbonRank.jsx'))

/**
 * ViewChart — one `charts[]` block from a product's screen, drawn.
 *
 *   {title, kind: 'line'|'bar'|'donut'|'flow', labels[], series:[{name, values[]}], unit, stacked,
 *    caption, y_min, y_max, links: [{source, target, value}] (flow only)}
 *
 * The product sends plain numbers; `unit` is display-only. `null` means "no data for that
 * point": a line breaks there and a bar is absent — never plotted as zero, because a zero is a
 * claim the product did not make.
 *
 * WHICH PICTURES, from the shape of the data alone (the first is shown, the rest are tabs, and
 * "Table" — every number as text — is always there):
 *   a figure per country           Map · Regions (ring of regions, globe inside) · Mosaic (circle of
 *                                  countries) · Flow (all → region → country) · the bars or donut
 *   parts of a whole (donut)       Donut · Arch · Rose · Treemap · Waffle — each labelled on the picture
 *   one series of bars             Bars · Radial (rings on a dial) · Treemap
 *   stacked bars                   Bars · Flow (each series into each bar)
 *   a line over time               an area with its peak marked · Ranking (ribbons) for 3-8 series
 *   flow (links)                   Flow
 * Regions, Mosaic, Flow and Treemap share out a whole, so they are offered only for figures that
 * add up — never for averages, rates or percentages.
 *
 * Under the title, one plain sentence says what the chart shows (see charts/insight.js).
 */
const INK = '#52514e'
const GRID = '#ecebe6'

const VIEWS = {
  map: ['Map', MapIcon], regions: ['Regions', Orbit], mosaic: ['Mosaic', Hexagon], flow: ['Flow', Waypoints],
  bars: ['Bars', ChartColumn], donut: ['Donut', ChartPie], arch: ['Arch', Rainbow], rose: ['Rose', Flower2],
  tree: ['Treemap', LayoutGrid], waffle: ['Waffle', Grid3x3], radial: ['Radial', Gauge], chart: ['Chart', ChartArea],
  rank: ['Ranking', ListOrdered], table: ['Table', Table2],
  status: ['Status', ClipboardCheck], readymap: ['Readiness', MapIcon], heat: ['Heatmap', Flame],
}

// A bound counts only when it is a real number. A string, null or NaN is not a bound.
const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null)

/** The value axis. With no bounds it is what it always was: from zero, up to whatever the data
 *  needs. A bound the product sends replaces that end — and if the data goes past it, the axis
 *  still stretches to show it, because hiding a value to honour a range would be worse. With BOTH
 *  bounds, evenly spaced ticks are chosen so a 0-5 scale reads 0 1 2 3 4 5 rather than 0 2 4 5. */
function valueAxis(chart) {
  const lo = num(chart?.y_min), hi = num(chart?.y_max)
  const out = { domain: [lo ?? 0, hi ?? 'auto'] }
  if (lo !== null && hi !== null && hi > lo) {
    const span = hi - lo
    const mag = Math.pow(10, Math.floor(Math.log10(span / 5)))
    const step = [1, 2, 2.5, 5, 10].map(m => m * mag).find(st => span / st <= 5) || span
    const ticks = []
    for (let v = lo; v <= hi + step / 1000; v += step) ticks.push(Math.round(v * 1000) / 1000)
    if (ticks.length <= 12) out.ticks = ticks
  }
  return out
}

const fmt = (v, unit) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—'
  const n = Number(v)
  const s = (Math.round(n * 100) / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })
  if (unit === '$M') return `$${s}M`
  if (unit === '$') return `$${s}`
  return `${s}${unit || ''}`
}

/** 100 squares shared out between the slices by largest remainder, so they always add to exactly
 *  100 and a slice that is real never rounds away to nothing unless it is under half a percent. */
function waffleCells(slices) {
  const total = slices.reduce((a, s) => a + Number(s.value), 0)
  if (!(total > 0)) return []
  const raw = slices.map(s => (Number(s.value) / total) * 100)
  const count = raw.map(Math.floor)
  let left = 100 - count.reduce((a, b) => a + b, 0)
  raw.map((r, i) => [r - Math.floor(r), i]).sort((a, b) => b[0] - a[0]).forEach(([, i]) => { if (left > 0) { count[i]++; left-- } })
  return count.flatMap((n, i) => Array(n).fill(i))
}

// Labels that are points in time or steps on a scale: their order matters, so they are never
// re-arranged by size (no treemap, no rose), and their story is a trend, not a leader.
const MONTH = /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i
const isOrdered = (labels) => labels.length > 0 && labels.filter(l => {
  const s = String(l).trim()
  return MONTH.test(s) || /^\d{1,4}$/.test(s) || /^(fy|q[1-4]|w|wk|week)\s?\d/i.test(s) || /^\d{1,2}[/.-]\d{1,2}/.test(s)
}).length / labels.length >= 0.6

/** A dot and a small boxed note at the highest point of a line: "Peak · 104 in Sep". */
function PeakNote({ viewBox, text, color }) {
  if (!viewBox) return null
  const { x, y } = viewBox
  const w = text.length * 6.1 + 14
  const above = y > 46
  const by = above ? y - 34 : y + 14
  return (
    <g pointerEvents="none">
      <line x1={x} y1={above ? y - 7 : y + 7} x2={x} y2={above ? by + 20 : by} stroke={color} strokeWidth="1.25" />
      <rect x={x - w / 2} y={by} width={w} height={20} rx={10} fill="#ffffff" stroke={color} strokeWidth="1.25" />
      <text x={x} y={by + 14} textAnchor="middle" fontSize="11" fontWeight="700" fill="#1F2937">{text}</text>
    </g>
  )
}

export default function ViewChart({ chart, theme = themeFor() }) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '')
  const COLOURS = theme.series
  const labels = useMemo(() => (Array.isArray(chart?.labels) ? chart.labels : []), [chart])
  const series = useMemo(() => (Array.isArray(chart?.series) ? chart.series : [])
    .map(s => ({ name: String(s?.name ?? ''), values: Array.isArray(s?.values) ? s.values : [] })), [chart])
  const kind = chart?.kind
  // A series NAMED for a status ("Ready", "Partly", "Missing") takes that status's colour, the same
  // one the status pills in the tables use, so a colour never means two things on one screen.
  const colour = (i) => {
    const st = kind !== 'donut' ? statusKind(series[i]?.name) : null
    return st ? STATUS_LOOK[st].dot : COLOURS[Math.min(i, COLOURS.length - 1)]
  }
  const unit = chart?.unit || ''
  const fmtV = (v) => fmt(v, unit)
  const caption = typeof chart?.caption === 'string' ? chart.caption.trim() : ''
  const yAxis = valueAxis(chart)
  const [chosen, setChosen] = useState(null)

  const flowLinks = useMemo(() => (kind === 'flow' ? linkFlow(chart?.links, COLOURS) : null), [kind, chart, COLOURS])
  const ordered = isOrdered(labels)
  const one = series.length === 1
  // The parts of the one series: what the donut, treemap, radial and country views are made of.
  const pieces = useMemo(() => (one ? labels.map((l, i) => ({ name: String(l), value: series[0].values[i] ?? null })) : [])
    .filter(p => p.value !== null && p.value !== undefined && Number(p.value) > 0)
    .map((p, i) => ({ ...p, value: Number(p.value), color: COLOURS[Math.min(i, COLOURS.length - 1)] })), [one, labels, series, COLOURS])
  const isCountry = (kind === 'bar' || kind === 'donut') && one && looksLikeCountries(labels)
  const countryItems = useMemo(() => (isCountry ? labels.map((l, i) => ({ name: String(l), value: series[0].values[i] ?? null })) : []), [isCountry, labels, series])
  // Sharing out a whole only makes sense for figures that add up.
  const additive = kind === 'donut' || (unit !== '%' && additiveName(`${chart?.title || ''} ${series[0]?.name || ''}`))
  const canRank = kind === 'line' && series.length >= 3 && series.length <= 8 && labels.length >= 3

  let views
  if (kind === 'flow') views = ['flow', 'table']
  else if (kind === 'line') views = ['chart', ...(canRank ? ['rank'] : []), 'table']
  else if (isCountry) views = ['map', ...(additive && pieces.length >= 3 ? ['regions', 'mosaic', 'flow'] : []), kind === 'donut' ? 'donut' : 'bars', 'table']
  else if (kind === 'donut') {
    views = ['donut', ...(pieces.length <= 8 ? ['arch'] : []), ...(!ordered && pieces.length >= 3 && pieces.length <= 12 ? ['rose'] : []),
      ...(!ordered && pieces.length >= 3 ? ['tree'] : []), 'waffle', 'table']
  } else if (one) {
    views = ['bars', ...(pieces.length >= 2 && pieces.length <= 8 ? ['radial'] : []), ...(!ordered && additive && pieces.length >= 3 ? ['tree'] : []), 'table']
  } else views = ['bars', ...(chart?.stacked && additive ? ['flow'] : []), 'table']
  const mode = views.includes(chosen) ? chosen : views[0]
  const wide = isCountry || kind === 'flow' || canRank

  const flowData = useMemo(() => {
    if (mode !== 'flow') return null
    if (kind === 'flow') return flowLinks
    if (isCountry) return regionFlow(countryItems, `All · ${series[0]?.name || chart?.title || 'total'}`)
    return seriesFlow(labels, series, COLOURS)
  }, [mode, kind, flowLinks, isCountry, countryItems, series, chart, labels, COLOURS])

  // Nothing to plot: say so, as the product does for its own empty sections.
  const hasData = kind === 'flow' ? !!flowLinks : series.some(s => s.values.some(v => v !== null && v !== undefined))
  if (!['line', 'bar', 'donut', 'flow'].includes(kind) || !hasData) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3">
        {chart?.title && <p className="text-sm font-semibold text-gray-800">{chart.title}</p>}
        <p className="text-xs text-gray-500 mt-1">No data to chart yet.</p>
      </div>
    )
  }

  // The one sentence under the title.
  let insight = null
  if (kind === 'line') insight = mode === 'rank' ? rankInsight(labels, series) : trendInsight(labels, series, fmtV)
  else if (kind === 'flow') {
    const top = [...flowLinks.links].sort((a, b) => b.value - a.value)[0]
    insight = top ? `The largest flow is ${top.source} → ${top.target}: ${fmtV(top.value)}.` : null
  } else if (isCountry) {
    insight = [leaderInsight(countryItems, fmtV), additive ? regionInsight(groupByRegion(countryItems)) : null].filter(Boolean).join(' ') || null
  } else if (kind === 'donut') insight = shareInsight(pieces)
  else if (one) insight = ratingInsight(labels, series[0].values, series[0].name) || (ordered ? trendInsight(labels, series, fmtV) : leaderInsight(pieces, fmtV))

  const rows = labels.map((l, i) => {
    const row = { label: String(l) }
    series.forEach(s => { row[s.name] = s.values[i] ?? null })
    return row
  })

  const tooltip = (
    <Tooltip
      cursor={{ fill: 'rgba(26,26,26,0.04)' }}
      formatter={(v, name) => [fmtV(v), name]}
      contentStyle={{ fontSize: 12, borderRadius: 10, borderColor: GRID, boxShadow: '0 6px 20px rgba(0,0,0,.10)' }}
    />
  )
  const axis = { tick: { fontSize: 11, fill: INK }, stroke: GRID }
  const grad = (i) => `g${uid}${i}`
  const gradients = (
    <defs>
      {series.map((s, i) => (
        <linearGradient key={i} id={grad(i)} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={colour(i)} stopOpacity={kind === 'line' ? 0.38 : 1} />
          <stop offset="100%" stopColor={colour(i)} stopOpacity={kind === 'line' ? 0.02 : 0.72} />
        </linearGradient>
      ))}
    </defs>
  )
  const lazyBox = (node) => <Suspense fallback={<p className="text-xs text-gray-400" style={{ height: 260 }}>Drawing…</p>}>{node}</Suspense>
  const metric = series[0]?.name || chart?.title || ''

  let plot
  if (mode === 'map') {
    plot = lazyBox(<WorldMap items={countryItems} theme={theme} unit={unit} fmt={fmt} metric={metric} />)
  } else if (mode === 'regions') {
    plot = lazyBox(<RegionWheel items={countryItems} fmt={fmtV} metric={metric} uid={`${uid}w`} />)
  } else if (mode === 'mosaic') {
    plot = lazyBox(<Mosaic items={countryItems} fmt={fmtV} metric={metric} uid={`${uid}m`} />)
  } else if (mode === 'flow') {
    plot = flowData ? lazyBox(<Flow data={flowData} fmt={fmtV} uid={`${uid}f`} />) : <p className="text-xs text-gray-500">Nothing to show as a flow.</p>
  } else if (mode === 'rank') {
    plot = lazyBox(<RibbonRank periods={labels.map(String)} series={series} colors={series.map((_, i) => colour(i))} fmt={fmtV} uid={`${uid}r`} />)
  } else if (mode === 'donut') {
    plot = <Donut pieces={pieces} fmt={fmtV} percent={unit === '%'} />
  } else if (mode === 'arch') {
    plot = <Arch pieces={pieces} fmt={fmtV} percent={unit === '%'} />
  } else if (mode === 'rose') {
    plot = <Rose pieces={pieces} fmt={fmtV} />
  } else if (mode === 'radial') {
    const items = ordered ? pieces : [...pieces].sort((a, b) => b.value - a.value)
    plot = <RadialBars items={items} color={colour(0)} fmt={fmtV} />
  } else if (mode === 'waffle') {
    const cells = waffleCells(pieces)
    plot = (
      <div className="flex flex-col sm:flex-row items-center gap-5">
        <div className="w-full sm:w-1/2 flex justify-center">
          <div className="grid grid-cols-10 gap-1 w-56 max-w-full" role="img" aria-label="Each square is one percent of the total">
            {cells.map((idx, k) => (
              <span key={k} title={`${pieces[idx].name} — ${fmtV(pieces[idx].value)}`} className="aspect-square rounded-[3px]"
                    style={{ background: pieces[idx].color, animation: 'pt-rise .5s both', animationDelay: `${k * 8}ms` }} />
            ))}
          </div>
        </div>
        <ul className="w-full sm:w-1/2 space-y-1.5">
          {pieces.map((s, i) => (
            <li key={i} className="flex items-center gap-2 text-xs text-gray-700">
              <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: s.color }} />
              <span className="flex-1 break-words">{s.name}</span>
              <span className="tabular-nums text-gray-900 font-semibold">{fmtV(s.value)}</span>
            </li>
          ))}
        </ul>
      </div>
    )
  } else if (mode === 'tree') {
    // Up to six parts take the categorical colours in order, the same as the donut, so a part keeps
    // its colour across views. Past six, every tile takes the one accent colour: more hues would
    // mean nothing, and the names are printed on the tiles.
    const total = pieces.reduce((a, p) => a + p.value, 0)
    const many = pieces.length > 6
    const data = [...pieces].sort((a, b) => b.value - a.value).map(p => ({ name: p.name, size: p.value, share: total ? p.value / total : 0, color: p.color }))
    const fit = (name, width) => {
      const room = Math.max(3, Math.floor(width / 7.5) - 1)
      return String(name).length > room ? `${String(name).slice(0, room)}…` : String(name)
    }
    plot = (
      <div style={{ height: 300 }}>
        <ResponsiveContainer>
          <Treemap data={data} dataKey="size" nameKey="name" isAnimationActive animationDuration={700} stroke="#ffffff"
                   content={({ x, y, width, height, name, value, depth, color }) => {
                     if (depth !== 1 || !(width > 0) || !(height > 0)) return null
                     const roomy = width > 64 && height > 38
                     const share = total ? Math.round((Number(value) / total) * 100) : 0
                     return (
                       <g>
                         <rect x={x + 1} y={y + 1} width={Math.max(0, width - 2)} height={Math.max(0, height - 2)} rx={6}
                               fill={many ? COLOURS[0] : color} fillOpacity={many ? 0.55 + 0.45 * (Number(value) / (data[0]?.size || 1)) : 1} />
                         {roomy && (
                           <>
                             <text x={x + 9} y={y + 19} fontSize={12} fontWeight={700} fill="#ffffff" stroke="none">{fit(name, width)}</text>
                             <text x={x + 9} y={y + 35} fontSize={11} fill="#ffffff" fillOpacity={0.92} stroke="none">{unit === '%' ? fmtV(value) : `${fmtV(value)} · ${share}%`}</text>
                           </>
                         )}
                       </g>
                     )
                   }}>
            <Tooltip formatter={(v, n, p) => [`${fmtV(v)} (${Math.round((p?.payload?.share || 0) * 100)}%)`, p?.payload?.name || n]}
                     contentStyle={{ fontSize: 12, borderRadius: 10, borderColor: GRID, boxShadow: '0 6px 20px rgba(0,0,0,.10)' }} />
          </Treemap>
        </ResponsiveContainer>
      </div>
    )
  } else if (mode === 'bars') {
    // Sideways when the names would not fit under the bars: many bars, or long ones.
    const sideways = one && (labels.length > 7 || labels.some(l => String(l).length > 12))
    const labelWidth = Math.min(170, Math.max(60, Math.max(...labels.map(l => String(l).length)) * 6.4 + 12))
    const showValues = one && labels.length <= 14
    plot = (
      <div style={{ height: sideways ? Math.max(240, labels.length * 34 + 30) : 270 }}>
        <ResponsiveContainer>
          <BarChart data={rows} layout={sideways ? 'vertical' : 'horizontal'} margin={{ top: 18, right: sideways ? 56 : 12, left: 0, bottom: 0 }}>
            {gradients}
            <CartesianGrid stroke={GRID} vertical={sideways} horizontal={!sideways} />
            {sideways ? (
              <>
                <XAxis type="number" {...axis} {...yAxis} tickFormatter={fmtV} />
                <YAxis type="category" dataKey="label" {...axis} width={labelWidth} interval={0} />
              </>
            ) : (
              <>
                <XAxis dataKey="label" {...axis} />
                <YAxis {...axis} {...yAxis} tickFormatter={fmtV} width={56} />
              </>
            )}
            {tooltip}
            {series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
            {series.map((s, i) => (
              <Bar key={s.name} dataKey={s.name} fill={`url(#${grad(i)})`} stroke={colour(i)} strokeWidth={0}
                   radius={sideways ? [0, 7, 7, 0] : (chart.stacked ? 0 : [7, 7, 0, 0])} maxBarSize={sideways ? 22 : 56}
                   stackId={chart.stacked ? 'a' : undefined} animationDuration={900}>
                {showValues && <LabelList dataKey={s.name} content={({ x, y, width, height, value }) => (value === null || value === undefined ? null : (
                  <text x={sideways ? x + width + 6 : x + width / 2} y={sideways ? y + height / 2 : y - 6} textAnchor={sideways ? 'start' : 'middle'}
                        dominantBaseline={sideways ? 'central' : 'auto'} fontSize="11" fontWeight="600" fill="#1A1A1A">{fmtV(value)}</text>))} />}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    )
  } else if (mode === 'chart') {
    // The highest point of each of the first two series is marked and named; the last point of
    // each says its figure. Selective, so the picture is not buried in numbers.
    const peaks = series.slice(0, 2).map((s, i) => {
      let at = -1
      s.values.forEach((v, k) => { if (num(Number(v)) !== null && v !== null && (at < 0 || Number(v) > Number(s.values[at]))) at = k })
      return at >= 0 ? { i, at, v: Number(s.values[at]) } : null
    }).filter(Boolean)
    const lastOf = (s) => { for (let k = s.values.length - 1; k >= 0; k--) if (s.values[k] !== null && s.values[k] !== undefined) return k; return -1 }
    plot = (
      <div style={{ height: 290 }}>
        <ResponsiveContainer>
          <AreaChart data={rows} margin={{ top: 40, right: 46, left: 0, bottom: 0 }}>
            {gradients}
            <CartesianGrid stroke={GRID} vertical={false} />
            <XAxis dataKey="label" {...axis} />
            <YAxis {...axis} {...yAxis} tickFormatter={fmtV} width={56} />
            {tooltip}
            {series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
            {series.map((s, i) => {
              const last = lastOf(s)
              return (
                <Area key={s.name} type="monotone" dataKey={s.name} stroke={colour(i)} strokeWidth={2.5}
                      fill={`url(#${grad(i)})`} dot={{ r: 3, strokeWidth: 2, fill: '#fff' }} activeDot={{ r: 5 }}
                      stackId={chart.stacked ? 'a' : undefined} connectNulls={false} animationDuration={1000}>
                  {series.length <= 3 && (
                    <LabelList dataKey={s.name} content={({ x, y, index, value }) => (index === last && value !== null && value !== undefined ? (
                      <text x={x + 8} y={y + 4} fontSize="11" fontWeight="700" fill={colour(i)}>{fmtV(value)}</text>) : null)} />
                  )}
                </Area>
              )
            })}
            {peaks.map(p => (
              <ReferenceDot key={p.i} x={String(labels[p.at])} y={p.v} r={5} fill={colour(p.i)} stroke="#ffffff" strokeWidth={2}
                            label={<PeakNote text={`Peak · ${fmtV(p.v)} in ${labels[p.at]}`} color={colour(p.i)} />} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    )
  }

  const table = kind === 'flow' ? (
    <table className="w-full text-xs">
      <thead><tr className="border-b border-gray-200">{['From', 'To', 'Amount'].map(h => <th key={h} className="text-left px-2 py-1.5 text-gray-500 font-semibold">{h}</th>)}</tr></thead>
      <tbody>
        {flowLinks.links.map((l, i) => (
          <tr key={i} className="border-b border-gray-50 last:border-0">
            <td className="px-2 py-1.5 text-gray-700">{l.source}</td><td className="px-2 py-1.5 text-gray-700">{l.target}</td>
            <td className="px-2 py-1.5 text-gray-700 tabular-nums">{fmtV(l.value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  ) : (
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b border-gray-200">
          <th className="text-left px-2 py-1.5 text-gray-500 font-semibold" />
          {series.map(s => <th key={s.name} className="text-left px-2 py-1.5 text-gray-500 font-semibold">{s.name}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, ri) => (
          <tr key={ri} className="border-b border-gray-50 last:border-0">
            <td className="px-2 py-1.5 text-gray-700 font-medium">{r.label}</td>
            {series.map(s => <td key={s.name} className="px-2 py-1.5 text-gray-700 tabular-nums">{fmtV(r[s.name])}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  )

  return (
    <div className={`pt-rise rounded-2xl border border-gray-200 bg-white px-4 py-4 shadow-xs hover:shadow-md transition-shadow ${wide ? 'lg:col-span-2' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2 mb-3">
        <div className="min-w-0">
          {chart.title && (
            <p className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <span className="w-1 h-4 rounded-full shrink-0" style={{ background: 'var(--pt-accent, #4F46E5)' }} aria-hidden="true" />
              {chart.title}
            </p>
          )}
          {insight && (
            <p className="mt-1 text-xs text-gray-600 flex items-start gap-1.5">
              <Sparkles className="w-3.5 h-3.5 mt-px shrink-0 text-[var(--pt-accent,#4F46E5)]" aria-hidden="true" />{insight}
            </p>
          )}
        </div>
        <ViewTabs views={views} mode={mode} onPick={setChosen} />
      </div>
      {mode === 'table' ? <div className="overflow-x-auto">{table}</div> : plot}
      {caption && <p className="mt-2 text-[11px] leading-snug text-gray-500">{caption}</p>}
    </div>
  )
}

/** The row of view buttons, each with a small picture of what it shows. Shared with the tables. */
export function ViewTabs({ views, mode, onPick }) {
  if (views.length < 2) return null
  return (
    <div className="shrink-0 inline-flex flex-wrap justify-end rounded-lg bg-gray-100 p-0.5" role="tablist">
      {views.map(key => {
        const [text, Icon] = VIEWS[key] || [key, Table2]
        const on = mode === key
        return (
          <button key={key} type="button" role="tab" aria-selected={on} onClick={() => onPick(key)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-md transition ${on ? 'bg-white shadow-xs text-gray-900' : 'text-gray-500 hover:text-gray-800'}`}>
            <Icon className="w-3.5 h-3.5" aria-hidden="true" />{text}
          </button>
        )
      })}
    </div>
  )
}
